import { z } from 'zod';
import { decimalPositivo, paginacion, uuid } from './comun.ts';

export const BANDEJAS_RETENCION = ['por_recibir', 'por_cargar', 'reintegros', 'revisar', 'historial'] as const;
export type BandejaRetencion = (typeof BANDEJAS_RETENCION)[number];
/** `anulada` = «Sin efecto» en pantalla: la factura quedó en cero por nota de crédito antes de que llegara el comprobante. */
export const ESTADOS_RETENCION = ['por_recibir', 'sin_cargar', 'recibida', 'reintegro_pendiente', 'reintegrada', 'revisar', 'anulada'] as const;
/**
 * Cómo se dice cada estado en pantalla y en los mensajes (hallazgo G1 del 2026-10-18). «Por cargar» y «Sin efecto»
 * son las palabras de la pantalla de Retenciones (re-auditoría 2026-10-18, ronda 2, hallazgo 2-H12).
 */
export const ESTADO_RETENCION_ETIQUETA: Record<(typeof ESTADOS_RETENCION)[number], string> = { por_recibir: 'por recibir', sin_cargar: 'por cargar', recibida: 'recibida', reintegro_pendiente: 'con reintegro pendiente', reintegrada: 'reintegrada', revisar: 'por revisar', anulada: 'sin efecto' };
export type EstadoRetencion = (typeof ESTADOS_RETENCION)[number];

/**
 * Mensaje para la retención a posteriori de una factura que YA tiene su retención de ese tipo: dice lo que de verdad
 * se puede hacer con la que tiene, según su estado (re-auditoría 2026-10-18, ronda 2, hallazgo 2-H12). Una «Sin
 * efecto» no tiene «Cargar comprobante»: primero la reabre la dueña o el contador con «Mandar a revisar». La usan la
 * API y la pantalla, para que digan lo mismo. `documento` es el número de la factura o «Esta factura».
 */
export function mensajeRetencionExistente(documento: string, tipo: 'iva' | 'municipal', r: { estado: string; comprobante_numero: string | null }): string {
  const cual = `${documento} ya tiene su retención ${tipo === 'iva' ? 'de IVA' : 'municipal'}`;
  if (r.estado === 'anulada') return `${cual}, pero quedó sin efecto: para cargarle el comprobante, la dueña o el contador la reabre con «Mandar a revisar» en el Historial de Retenciones y luego se carga desde la bandeja Revisar`;
  if (r.comprobante_numero) return `${cual} con el comprobante ${r.comprobante_numero} cargado: si hay que cambiarlo, la dueña o el contador usa «Corregir»`;
  const bandeja = r.estado === 'por_recibir' ? 'Por recibir' : r.estado === 'sin_cargar' ? 'Por cargar' : 'Revisar';
  return `${cual}: carga el comprobante con «Cargar comprobante» en la bandeja ${bandeja}`;
}
export const METODOS_REINTEGRO = ['saldo_favor', 'efectivo_usd', 'efectivo_bs', 'transferencia', 'zelle', 'pago_movil'] as const;
export type MetodoReintegro = (typeof METODOS_REINTEGRO)[number];

/** N° de comprobante SENIAT: AAAAMMNNNNNNNN (14 dígitos). */
export const comprobanteNumero = z
  .string()
  .trim()
  .regex(/^\d{14}$/, 'El comprobante tiene 14 dígitos: AAAAMMNNNNNNNN')
  .refine((n) => { const mes = Number(n.slice(4, 6)); const anio = Number(n.slice(0, 4)); return mes >= 1 && mes <= 12 && anio >= 2000 && anio <= 2100; }, 'Período del comprobante inválido (AAAAMM)');

export const comprobanteInput = z.object({
  comprobante_numero: comprobanteNumero,
  /** Fecha que trae el comprobante. */
  comprobante_fecha: z.iso.date(),
  /** Monto retenido según el comprobante, en BOLÍVARES (tolerancia ±1 Bs frente a lo calculado). */
  comprobante_monto_bs: decimalPositivo,
  /** Día en que la tienda recibió el comprobante: su mes es el del libro de ventas. Por defecto, hoy. */
  fecha_recepcion: z.iso.date().optional(),
});
export type ComprobanteInput = z.infer<typeof comprobanteInput>;

export const corregirComprobanteInput = comprobanteInput.extend({ motivo: z.string().trim().min(5, 'Explica la corrección').max(300) });
export type CorregirComprobanteInput = z.infer<typeof corregirComprobanteInput>;

export const reintegroInput = z.object({
  metodo: z.enum(METODOS_REINTEGRO),
  referencia: z.string().trim().max(60).optional(),
  observaciones: z.string().trim().max(300).optional(),
});
export type ReintegroInput = z.infer<typeof reintegroInput>;

/**
 * Retención a posteriori: el cliente trae un comprobante de una factura que se emitió sin retención (no estaba
 * marcado como agente de retención, o lo marcaron después), aunque ya esté cobrada completa.
 */
export const retencionPosteriorInput = comprobanteInput.extend({
  documento_id: uuid,
  tipo: z.enum(['iva', 'municipal']),
  /**
   * % retenido. Por defecto el normal: IVA 75; municipal, el propio del cliente o el del parámetro. Uno distinto
   * (IVA 100, otro municipal) solo lo indica quien administra la ficha fiscal de los clientes (`clientes.credito`).
   */
  porcentaje: decimalPositivo.optional(),
  /**
   * Dejar al cliente marcado como contribuyente especial (o con retención municipal) para las próximas facturas. Lo
   * aplica quien administra la ficha fiscal; si quien carga no lo es, se avisa a la administración para que lo marque.
   */
  marcar_cliente: z.boolean().default(true),
  observaciones: z.string().trim().max(300).optional(),
});
export type RetencionPosteriorInput = z.infer<typeof retencionPosteriorInput>;

/** Mandar una retención a «revisar» (contador o dueña): lleva la nota de qué hay que arreglar. */
export const enviarARevisionInput = z.object({ observaciones: z.string().trim().min(3).max(300) });

export const revisadaInput = z.object({
  /** Monto corregido tras la devolución (opcional; si no se indica se conserva). Nunca más que base × %. */
  monto: decimalPositivo.optional(),
  /** Dejarla sin efecto: el cliente ya no va a retener (solo si aún no hay comprobante). */
  sin_efecto: z.boolean().optional(),
  observaciones: z.string().trim().min(3).max(300),
});

export const retencionesFiltros = paginacion.extend({
  bandeja: z.enum(BANDEJAS_RETENCION).optional(),
  tipo: z.enum(['iva', 'municipal']).optional(),
  q: z.string().trim().max(80).optional(),
  sucursal_id: uuid.optional(),
});
export type RetencionesFiltros = z.infer<typeof retencionesFiltros>;

export interface RetencionFilaDTO {
  id: string;
  sucursal_id: string;
  sucursal_nombre: string;
  documento_id: string;
  documento_numero: string;
  documento_tipo: string;
  documento_control: string | null;
  emitido_en: string;
  cliente_id: string;
  cliente_razon_social: string;
  cliente_rif: string;
  tipo: 'iva' | 'municipal';
  modalidad: 'base25' | 'completo';
  base: string;
  porcentaje: string;
  monto: string;
  estado: EstadoRetencion;
  comprobante_numero: string | null;
  comprobante_fecha: string | null;
  /** Monto del comprobante en Bs (los cargados antes del bloque 9 lo traen en $ en `comprobante_monto`). */
  comprobante_monto_bs: string | null;
  comprobante_monto: string | null;
  /** Día en que se recibió; su mes (`periodo`) es el del libro de ventas. */
  fecha_recepcion: string | null;
  periodo: string | null;
  /** Base y retención en Bs (lo fiscal). */
  base_bs: string;
  monto_bs: string;
  /** Cargada después de emitir la factura, a un cliente que no estaba marcado. */
  posterior: boolean;
  version: number;
  cargada_por_nombre: string | null;
  cargada_en: string | null;
  reintegro_metodo: MetodoReintegro | null;
  reintegro_referencia: string | null;
  /** Lo que de verdad se le reintegró ($): lo retenido menos lo que las notas de crédito ya le habían devuelto. */
  reintegro_monto: string | null;
  /** Pago completo sin reintegrar: lo que queda por devolverle hoy ($). null si no aplica. */
  reintegrable: string | null;
  reintegro_en: string | null;
  reintegro_validado_por_nombre: string | null;
  reintegro_programado_en: string | null;
  revisada_por_nombre: string | null;
  revisada_en: string | null;
  observaciones: string | null;
  /** Días desde la emisión (antigüedad visible, alerta > 30). */
  dias: number;
  alerta_30: boolean;
  creado_en: string;
}

export interface ResumenRetencionesDTO {
  por_recibir: { cantidad: number; monto: string };
  por_cargar: { cantidad: number; monto: string };
  reintegros: { cantidad: number; monto: string; programados: number };
  revisar: { cantidad: number; monto: string };
  vencidas_30: number;
}

export interface VersionRetencionDTO {
  id: string;
  version: number;
  comprobante_numero: string | null;
  comprobante_fecha: string | null;
  comprobante_monto: string | null;
  comprobante_monto_bs: string | null;
  fecha_recepcion: string | null;
  periodo: string | null;
  motivo: string;
  usuario_nombre: string;
  creado_en: string;
}
